import { ArrowRight } from "lucide-react-native";
import { Pressable, Text, View } from "react-native";
import { CardSurface } from "@louise/moon-ui";
import { EMBER, colors, semanticColors } from "@louise/moon-ui/theme/tokens";
import { OrganicMiniIcon, type OrganicMiniIconKind } from "./OrganicMiniIcon";

export type TestingOptionCardAction = {
  label: string;
  onPress: () => void;
  primary?: boolean;
};

export type TestingOptionCardData = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  timing?: string;
  actions: TestingOptionCardAction[];
};

function getTestingIconKind(id: string): OrganicMiniIconKind {
  if (id === "amh" || id.includes("test-amh")) return "amh";
  if (id === "afc" || id.includes("follicle")) return "afc";
  if (id === "hormones" || id.includes("hormone")) return "hormones";
  if (id === "genome" || id === "carrier" || id.includes("genetic")) {
    return "genetics";
  }
  if (id.includes("pgt") || id.includes("karyotype")) return "genetics";
  if (id.includes("panel") || id.includes("semen") || id.includes("sperm")) {
    return "panel";
  }
  return "panel";
}

function MiniActionButton({
  label,
  onPress,
  primary,
}: TestingOptionCardAction) {
  return (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => ({
        flexDirection: "row",
        alignItems: "center",
        gap: 5,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: primary
          ? "transparent"
          : semanticColors.action.quiet.border,
        backgroundColor: primary
          ? pressed
            ? "rgba(92, 52, 32, 0.9)"
            : colors.softBrown
          : pressed
            ? semanticColors.overlay.pressed
            : semanticColors.action.quiet.background,
        paddingHorizontal: 10,
        paddingVertical: 7,
      })}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 11,
          lineHeight: 14,
          color: primary ? semanticColors.text.inverse : EMBER.colors.text,
          fontWeight: "700",
        }}
      >
        {label}
      </Text>
      <ArrowRight
        size={12}
        color={primary ? semanticColors.text.inverse : EMBER.colors.text}
        strokeWidth={1.8}
      />
    </Pressable>
  );
}

export function TestingOptionCard({
  item,
  width,
}: {
  item: TestingOptionCardData;
  width: number;
}) {
  return (
    <CardSurface
      variant="panel"
      style={{
        width,
        minHeight: 278,
        padding: 14,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        <View
          style={{
            width: 34,
            height: 34,
            borderRadius: 999,
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <OrganicMiniIcon kind={getTestingIconKind(item.id)} size={34} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 10,
              lineHeight: 13,
              color: EMBER.colors.textLight,
              fontWeight: "700",
              letterSpacing: 0.4,
              textTransform: "uppercase",
            }}
          >
            {item.eyebrow}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              lineHeight: 19,
              color: EMBER.colors.text,
              fontWeight: "700",
              marginTop: 2,
            }}
          >
            {item.title}
          </Text>
        </View>
      </View>

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 12,
          lineHeight: 18,
          color: EMBER.colors.textMid,
          fontWeight: "400",
          marginTop: 12,
        }}
      >
        {item.body}
      </Text>

      {item.timing ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 10,
            lineHeight: 13,
            color: EMBER.colors.textLight,
            fontWeight: "700",
            letterSpacing: 0.35,
            textTransform: "uppercase",
            marginTop: 9,
          }}
        >
          {item.timing}
        </Text>
      ) : null}

      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 7,
          marginTop: "auto",
          paddingTop: 14,
        }}
      >
        {item.actions.map((action) => (
          <MiniActionButton key={action.label} {...action} />
        ))}
      </View>
    </CardSurface>
  );
}
